import { APIError } from "../../api/utils/errors";

import type { CasePrepUscisFilledForm } from "./types";

/**
 * Whether the form ever produced a PDF. A slot seeded with the case has no
 * pointer and no run, so there is nothing to fetch and nothing "gone".
 */
export const hasGeneratedPdf = (
  form: Pick<
    CasePrepUscisFilledForm,
    | "generated_pdf_url"
    | "generated_pdf_file_id"
    | "generated_pdf_storage_path"
    | "run_steps"
  >
): boolean =>
  Boolean(
    form.generated_pdf_url ||
      form.generated_pdf_file_id ||
      form.generated_pdf_storage_path ||
      form.run_steps?.length
  );

/**
 * How the form pane should read a failed PDF fetch.
 *
 * `gone`: the record still advertises a PDF but the server has none left to
 * serve (the provider dropped it after its retention window). The answers are
 * intact, so the pane offers to regenerate instead of showing an error.
 * `error`: anything else, with the server's message when it sent one.
 */
export type GeneratedPdfLoadFailure =
  | { kind: "gone" }
  | { kind: "error"; message: string };

export const classifyGeneratedPdfLoadFailure = (
  error: unknown
): GeneratedPdfLoadFailure => {
  if (error instanceof APIError && error.status === 404)
    return { kind: "gone" };
  return {
    kind: "error",
    message:
      error instanceof Error && error.message
        ? error.message
        : "Unable to load generated PDF."
  };
};
